import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { AssignedStudent, MostRestrictiveLicense, Transaction, TransactionApi } from 'apis/TransactionApi';
import { useStudentDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { StudentGmetrixTable } from 'components/StudentGmetrixTable/StudentGmetrixTable';
import { StudentGroupApi, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { Student, StudentApi, StudentSortField } from 'apis/StudentApi';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { getMostRestrictedLicenseDeliverable } from 'utils/objects';
import GMetrixBanner from 'components/GMetrixBanner/GMetrixBanner';
import { TransactionPermissions } from 'pages/access_codes/types';
import type { ReportPermissions } from 'pages/reports/types';
import { GroupDetailsContext } from 'pages/groups/context';
import { StudentPermissions } from 'pages/students/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { GroupPermissions } from 'pages/groups/types';
import { Col, Container, Row } from 'react-bootstrap';
import { useNotification } from 'app/notifications';
import { SortDescriptor } from 'utils/dataQuery';
import { useContext } from 'components/Tabs';
import { useSuperState } from 'hooks';
import { ListItemCount } from 'types';

import 'components/StudentGMetrixTableDialog/student-gmetrix-table-dialog.scss';

type Option = 'Add' | 'Remove' | undefined;

export interface StudentAssociation {
    associationIds: number[];
    associationName: 'Transaction' | 'Group' | 'Report';
}

export type StudentPermissionLike = TransactionPermissions | StudentPermissions | GroupPermissions | ReportPermissions;

export interface StudentGMetrixTableDialogShowOptions {
    actionButtonText?: string;
    customSuccessButtonText?: string;
    dialogCustomTitle?: string;
    groupInfo?: { leastRedemptions: number; studentLength: number };
    multiSelect?: boolean;
    option?: Option;
    partnerId?: number;
    removeTheseStudents?: number[];
    studentAssociation?: StudentAssociation;
}

export interface StudentGMetrixTableDialogResult {
    ids: number[];
    search?: string;
    students?: Student[];
}

export interface StudentGMetrixTableDialogHandle {
    hide: () => void;
    show: (overrides?: StudentGMetrixTableDialogShowOptions) => Promise<StudentGMetrixTableDialogResult | null>;
}

interface StudentGMetrixTableDialogProps {
    actionButtonText?: string;
    customSuccessButtonText?: string;
    dialogCustomTitle?: string;
    groupInfo?: { leastRedemptions: number; studentLength: number };
    multiSelect?: boolean;
    option?: Option;
    partnerId?: number;
    permissions: StudentPermissionLike;
    removeTheseStudents?: number[];
    setStudentKendoCount?: React.Dispatch<React.SetStateAction<ListItemCount>>;
    showCancelButtonInDialog?: boolean;
    studentAssociation?: StudentAssociation;
}

export const StudentGMetrixTableDialog = forwardRef<StudentGMetrixTableDialogHandle, StudentGMetrixTableDialogProps>((props, forwardedRef) => {

    const {
        actionButtonText: propActionButtonText,
        customSuccessButtonText: propCustomSuccessButtonText,
        dialogCustomTitle: propDialogCustomTitle,
        groupInfo: propGroupInfo,
        multiSelect: propMultiSelect = false,
        option: propOption,
        partnerId: propPartnerId,
        permissions,
        removeTheseStudents: propRemoveTheseStudents,
        setStudentKendoCount,
        showCancelButtonInDialog = true,
        studentAssociation: propStudentAssociation
    } = props;

    const [state, setState] = useSuperState({
        groupStudentList: [] as Student[],
        loadingGridData: true,
        overrides: null as StudentGMetrixTableDialogShowOptions | null,
        paginationSkip: 0,
        paginationTake: 15,
        restrictedLicenseObj: { restriction: undefined as MostRestrictiveLicense | undefined, show: false },
        searchTerm: '',
        selectedStudents: [] as Student[],
        serverTotalCount: 0,
        sortDir: '',
        sortField: '' as StudentSortField | '',
        students: [] as Student[],
        transactionStudentList: [] as AssignedStudent[]
    });
    const {
        groupStudentList, loadingGridData, overrides, paginationSkip, paginationTake, restrictedLicenseObj,
        searchTerm, selectedStudents, serverTotalCount, sortDir, sortField, students, transactionStudentList
    } = state;
    const isOpen = overrides !== null;
    const studentDefaultLookupSearchFields = useStudentDefaultLookupSearchFields();

    const { pageContext, update } = useContext(GroupDetailsContext);
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const gmetrixBannerRef = useRef<any>();
    const resolverRef = useRef<((value: StudentGMetrixTableDialogResult | null) => void) | null>(null);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resolvedOption = overrides?.option ?? propOption;
    const resolvedMultiSelect = overrides?.multiSelect ?? propMultiSelect;
    const resolvedAssociation = overrides?.studentAssociation ?? propStudentAssociation;

    // Testing Centers (IsPostFiltering) load ALL students and filter client-side in real time.
    // Every other partner type has too many students for that, so we search server-side using the
    // same paged endpoint as the Students page. GetAllPaged picks the correct proc by the user's
    // role/managed partner type server-side, so the dialog stays in sync with the Students page.
    // The Remove flow always stays client-side because it operates on the small, already-scoped
    // set of students that belong to the transaction/group.
    const useServerSearch = !permissions.IsPostFiltering && resolvedOption !== 'Remove';

    // Scope every sub-call to the record's owning testing center, not the left-menu Selected Partner.
    const resolvedPartnerId = overrides?.partnerId ?? propPartnerId ?? null;
    const resolvedGroupInfo = overrides?.groupInfo ?? propGroupInfo;
    const resolvedRemoveTheseStudents = overrides?.removeTheseStudents ?? propRemoveTheseStudents;
    const resolvedActionButtonText = overrides?.actionButtonText ?? propActionButtonText ?? '';
    const resolvedCustomSuccessButtonText = overrides?.customSuccessButtonText ?? propCustomSuccessButtonText;
    const resolvedDialogCustomTitle = overrides?.dialogCustomTitle ?? propDialogCustomTitle;

    /** Settle the promise, reset state, and close the dialog. */
    const settle = useCallback((value: StudentGMetrixTableDialogResult | null) => {

        const resolve = resolverRef.current;
        resolverRef.current = null;
        setState({
            groupStudentList: [],
            overrides: null,
            paginationSkip: 0,
            restrictedLicenseObj: { restriction: undefined, show: false },
            searchTerm: '',
            selectedStudents: [],
            serverTotalCount: 0,
            sortDir: '',
            sortField: '',
            students: [],
            transactionStudentList: []
        });
        resolve?.(value);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(forwardedRef, () => ({
        hide: () => settle(null),
        show: (nextOverrides?: StudentGMetrixTableDialogShowOptions) => new Promise<StudentGMetrixTableDialogResult | null>(resolve => {

            // Cancel any in-flight pick.
            resolverRef.current?.(null);
            resolverRef.current = resolve;
            setState({ overrides: nextOverrides ?? {} });

        })
    }), [settle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve pending promise on unmount.
    useEffect(() => () => {

        resolverRef.current?.(null);
        resolverRef.current = null;

    }, []);

    // Group-size guardrail.
    useEffect(() => {

        const maxStudentNumber = pageContext?.Data.groupDetails?.MaxStudents;
        if (
            gmetrixBannerRef &&
            gmetrixBannerRef.current &&
            resolvedGroupInfo &&
            maxStudentNumber &&
            maxStudentNumber < resolvedGroupInfo.studentLength + selectedStudents.length &&
            maxStudentNumber <= resolvedGroupInfo.leastRedemptions + resolvedGroupInfo.studentLength + selectedStudents.length
        ) {

            gmetrixBannerRef.current.createBanner(formatMessage({ id: 'cannot_add_more_students_than_allowed.988689dd', defaultMessage: 'Cannot add more students than allowed' }), 'danger', true);
            setState(s => ({ selectedStudents: s.selectedStudents.slice(0, -1) }));

        }

    }, [selectedStudents, resolvedGroupInfo, pageContext?.Data.groupDetails?.MaxStudents, formatMessage({ id: 'cannot_add_more_students_than_allowed.988689dd', defaultMessage: 'Cannot add more students than allowed' })]);

    // Load grid data on open.
    useEffect(() => {

        if (!isOpen) {

            setState({ loadingGridData: false });
            return;

        }
        if (useServerSearch) {

            setState({ loadingGridData: true });
            fetchStudentsPaged(0, paginationTake, '', '', '', false);
            return;

        }
        if (resolvedOption) {

            setState({ loadingGridData: true });
            handleGMetrixTableData(resolvedOption);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps

    }, [isOpen, resolvedOption, useServerSearch]);

    // Debounced server-side search for roles that page students (non–Testing Center Report picker).
    useEffect(() => {

        if (!isOpen || !useServerSearch) return;

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }

        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchStudentsPaged(0, paginationTake, sortField, sortDir, searchTerm, false);

        }, 1000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };
        // eslint-disable-next-line react-hooks/exhaustive-deps

    }, [searchTerm]);

    const fetchStudentsPaged = async (skip: number, take: number, sField: string, sDir: string, search: string, archived: boolean) => {

        try {

            setState({ loadingGridData: true });
            const page = Math.floor(skip / take) + 1;
            const result = await StudentApi.GetAllPaged(page, take, sField as StudentSortField | '', sDir, search, archived);

            // The Add flow must hide students already associated with the transaction/group. The paged
            // proc doesn't know about that exclusion, so filter them out of each page client-side.
            const excludeSet = resolvedOption === 'Add' && resolvedRemoveTheseStudents
                ? new Set(resolvedRemoveTheseStudents)
                : null;
            const items = excludeSet ? result.Items.filter(student => !excludeSet.has(student.Id)) : result.Items;
            setState({ serverTotalCount: result.TotalCount, students: items });

        } catch (e) {

            console.error(e);
            createToast(formatMessage({ id: 'something_went_wrong.faaab954', defaultMessage: 'Something went wrong!' }), 'danger');

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchStudentsPaged(skip, take, sortField, sortDir, searchTerm, false);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort.length > 0 ? sort[0].field : '') as StudentSortField | '';
        const newSortDir = sort.length > 0 ? (sort[0].dir || '') : '';
        setState({ sortDir: newSortDir, sortField: newSortField });
        fetchStudentsPaged(paginationSkip, paginationTake, newSortField, newSortDir, searchTerm, false);

    };

    const deduplicateById = <T extends { Id: number }>(items: T[]): T[] => [...new Map(items.map(item => [item.Id, item])).values()];

    const updateStudentKendoCount = (studentList: Student[]) => {

        if (setStudentKendoCount && studentList.length > 0) {

            setStudentKendoCount({ count: studentList.length, firstId: studentList[0].Id });

        }

    };

    const handleGMetrixTableData = async (option: Option) => {

        if (!isOpen || !resolvedAssociation) return;

        const studentListInTable = await StudentApi.GetAll();

        switch (option) {

            case 'Add': {

                const studentFilteredList = resolvedRemoveTheseStudents
                    ? studentListInTable.filter(student => !resolvedRemoveTheseStudents.includes(student.Id))
                    : studentListInTable;

                setState({ students: studentFilteredList });
                updateStudentKendoCount(studentFilteredList);
                break;

            }
            case 'Remove': {

                const removeableStudentsInlist: Student[] = [];
                const initialTransactionStudentList: AssignedStudent[] = [];
                const initialGroupStudentList: Student[] = [];
                const studentIdSet = new Set<number>();

                if (resolvedAssociation.associationName === 'Transaction') {

                    const studentPromises = resolvedAssociation.associationIds.map(id => TransactionApi.GetStudentsByTransactionId(id, 1, resolvedPartnerId).catch(console.error));
                    const results = await Promise.all(studentPromises);

                    results.forEach(assignedStudents => {

                        if (assignedStudents) {

                            initialTransactionStudentList.push(...assignedStudents);
                            assignedStudents.forEach(s => studentIdSet.add(s.Id));

                        }

                    });
                    setState({ transactionStudentList: deduplicateById(initialTransactionStudentList) });

                } else if (resolvedAssociation.associationName === 'Group') {

                    const studentPromises = resolvedAssociation.associationIds.map(id => StudentGroupApi.GetAllStudents(id, resolvedPartnerId).catch(console.error));
                    const results = await Promise.all(studentPromises);

                    results.forEach(assignedStudents => {

                        if (assignedStudents) {

                            initialGroupStudentList.push(...assignedStudents);
                            assignedStudents.forEach(s => studentIdSet.add(s.Id));

                        }

                    });
                    setState({ groupStudentList: initialGroupStudentList });

                }

                studentListInTable.forEach(student => {

                    if (studentIdSet.has(student.Id)) {

                        removeableStudentsInlist.push(student);

                    }

                });

                const uniqueStudents = deduplicateById(removeableStudentsInlist);
                setState({ students: uniqueStudents });
                updateStudentKendoCount(uniqueStudents);
                break;

            }

        }
        setState({ loadingGridData: false });

    };

    const runNonReportConfirm = async (option: Option) => {

        if (!resolvedAssociation) return;

        const studentIds = selectedStudents.map(student => student.Id);
        const confirmPayload: StudentGMetrixTableDialogResult = { ids: studentIds, search: searchTerm };

        if (option === 'Add') {

            const maxStudentNumber = pageContext?.Data.groupDetails?.MaxStudents;
            const currentStudentNumber = pageContext?.Data.studentList?.length;

            // Soft over-limit check.
            if (currentStudentNumber && maxStudentNumber && selectedStudents.length + currentStudentNumber > maxStudentNumber) {

                createToast(formatMessage({ id: 'cannot_add_more_students_than_allowed.988689dd', defaultMessage: 'Cannot add more students than allowed' }), 'danger');
                settle(null);
                return;

            }

            const groupInfoAllowsAdd = resolvedGroupInfo
                && selectedStudents.length > 0
                && maxStudentNumber
                && resolvedGroupInfo.studentLength < maxStudentNumber;

            if (
                groupInfoAllowsAdd
                || maxStudentNumber === 0
                || maxStudentNumber === undefined
                || maxStudentNumber === -1
            ) {

                if (resolvedAssociation.associationName === 'Group') {

                    let transactionsForStudents: Transaction[] = [];
                    for (const groupId of resolvedAssociation.associationIds) {

                        const groupsTransactions = await StudentGroupApi.GetTransactionsFromGroup(groupId, resolvedPartnerId).catch(console.error);
                        if (groupsTransactions) {

                            transactionsForStudents = [...transactionsForStudents, ...groupsTransactions];

                        }

                    }
                    await StudentGroupApi.AddStudentToGroup(resolvedAssociation.associationIds, studentIds);
                    if (transactionsForStudents.length > 0) {

                        await TransactionApi.AddStudentToTransaction(
                            transactionsForStudents.map(transaction => transaction.Id),
                            studentIds,
                            resolvedPartnerId ?? undefined
                        ).catch(console.error);

                    }

                } else if (resolvedAssociation.associationName === 'Transaction') {

                    // License-capacity check: may keep the dialog open with a banner.
                    let mostRestrictedLicense: MostRestrictiveLicense | undefined;
                    const licenseObj = await TransactionApi.GetAllLicenses().catch(console.error);
                    for (const transactionId of resolvedAssociation.associationIds) {

                        const content = await TransactionApi.GetLimitedContentByTransactionId(transactionId, resolvedPartnerId).catch(console.error);
                        const transaction = await TransactionApi.Get(transactionId, resolvedPartnerId).catch(console.error);
                        if (licenseObj && content) {

                            const restrictedLicense = getMostRestrictedLicenseDeliverable(
                                licenseObj.licenses,
                                content,
                                (transaction && transaction.HasXED) === true
                            );
                            if (mostRestrictedLicense === undefined) {

                                mostRestrictedLicense = restrictedLicense;

                            } else if (
                                mostRestrictedLicense.avaliableStudentsAllowed === -1
                                && restrictedLicense.avaliableStudentsAllowed !== -1
                            ) {

                                mostRestrictedLicense = restrictedLicense;

                            } else if (
                                mostRestrictedLicense.avaliableStudentsAllowed < restrictedLicense.avaliableStudentsAllowed
                                && restrictedLicense.avaliableStudentsAllowed !== -1
                            ) {

                                mostRestrictedLicense = restrictedLicense;

                            }

                        }

                    }
                    if (
                        mostRestrictedLicense
                        && (studentIds.length <= mostRestrictedLicense.avaliableStudentsAllowed
                            || mostRestrictedLicense.avaliableStudentsAllowed === -1)
                    ) {

                        await TransactionApi.AddStudentToTransaction(resolvedAssociation.associationIds, studentIds, resolvedPartnerId ?? undefined);
                        setState({ restrictedLicenseObj: { restriction: undefined, show: false } });

                    } else {

                        // Stay open so the user can adjust.
                        setState({ restrictedLicenseObj: { restriction: mostRestrictedLicense, show: true } });
                        return;

                    }

                }

            } else {

                createToast(formatMessage({ id: 'cannot_add_more_students_than_allowed.988689dd', defaultMessage: 'Cannot add more students than allowed' }), 'danger');
                settle(null);
                return;

            }

        } else if (option === 'Remove') {

            if (resolvedAssociation.associationName === 'Group') {

                const ids = selectedStudents
                    .map(student => groupStudentList.find(gs => gs.Id === student.Id)?.Id)
                    .filter((id): id is number => id !== undefined);

                if (ids.length > 0) {

                    await Promise.all(resolvedAssociation.associationIds.map(groupId => StudentGroupApi.DeleteStudentFromGroup(groupId, ids)));

                }

            } else if (resolvedAssociation.associationName === 'Transaction') {

                const accessIds = selectedStudents
                    .map(student => transactionStudentList.find(ts => ts.Id === student.Id)?.StudentContentAccessId)
                    .filter((id): id is number => id !== undefined);

                if (accessIds.length > 0) {

                    await TransactionApi.RemoveStudentFromTransaction(accessIds);

                }

            }

        }

        // Refresh cached tab data.
        if (pageContext) {

            const refreshed = await StudentGroupApi.GetAllStudents(pageContext?.Id, resolvedPartnerId);
            update({ groupDetails: { ...pageContext.Data.groupDetails, StudentCount: refreshed.length } as StudentGroupWithDetails, studentList: refreshed });

        }

        settle(confirmPayload);

    };

    const handleConfirm = () => {

        if (!resolvedAssociation) return;

        if (resolvedAssociation.associationName === 'Report') {

            const payload: StudentGMetrixTableDialogResult = permissions.IsPostFiltering
                ? { ids: selectedStudents.map(s => s.Id), students: selectedStudents }
                : { ids: selectedStudents.map(s => s.Id), search: searchTerm, students: selectedStudents };
            settle(payload);
            return;

        }

        runNonReportConfirm(resolvedOption);

    };

    return (
        <GMetrixDialog
            dialogLabel={(
                <div className="d-flex align-items-center">
                    <div>
                        {resolvedDialogCustomTitle ? resolvedDialogCustomTitle : `${resolvedOption ?? ''} ${formatMessage({ id: 'students.c6d199ef', defaultMessage: 'Students', description: 'menu' })}`}
                    </div>
                </div>
            )}
            actionButtonClick={handleConfirm}
            actionButtonDisabled={selectedStudents.length === 0}
            actionButtonText={resolvedCustomSuccessButtonText ? resolvedCustomSuccessButtonText : resolvedActionButtonText}
            onCancel={() => settle(null)}
            show={isOpen}
            showCancelButton={showCancelButtonInDialog}
            size="xl"
        >
            <Container fluid className="student-gmetrix-table-dialog">
                {restrictedLicenseObj && restrictedLicenseObj.restriction && restrictedLicenseObj.show && (
                    <>
                        <Row>
                            <Col>
                                <div className="py-2 d-flex justify-content-center">
                                    <FormattedMessage defaultMessage="Error" id="error.e50d69ad" />
                                    {' '}
                                    -
                                    {restrictedLicenseObj.restriction.license.LicenseName}
                                    {' '}
                                    <FormattedMessage defaultMessage="is being used for some of the content in this Access Code and it only has" id="is_being_used_for_some_of_the_content_in.fedeb56b" />
                                    {restrictedLicenseObj.restriction.avaliableStudentsAllowed}
                                    {' '}
                                    <FormattedMessage defaultMessage="seats available" id="seats_available.d1ad13fb" />
                                    .
                                </div>
                                <div className="py-2 d-flex justify-content-center">
                                    <FormattedMessage defaultMessage="You can only add up to" id="you_can_only_add_up_to.5038baab" />
                                    {' '}
                                    {restrictedLicenseObj.restriction.avaliableStudentsAllowed}
                                    {' '}
                                    <FormattedMessage defaultMessage="students to this Access Code." id="students_to_this_access_code.92972378" />
                                </div>
                            </Col>
                        </Row>
                        <hr className="hr" />
                    </>
                )}
                <Row>
                    <GMetrixBanner ref={gmetrixBannerRef} />
                    <Col className="pb-3">
                        <StudentGmetrixTable
                            manuallyRenderStudents
                            selectableRows
                            columnData={[
                                { field: 'Firstname', sortField: 'FirstName', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) },
                                { field: 'Lastname', sortField: 'LastName', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) },
                                { field: 'Username', sortField: 'UserName', title: formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' }) },
                                { field: 'Email', sortField: 'EmailAddress', title: formatMessage({ id: 'email.5cbb5845', defaultMessage: 'Email', description: 'Formulario' }) },
                                { field: 'StudentNumber', title: formatMessage({ id: 'student_number.4ed64306', defaultMessage: 'Student Number' }) },
                                { field: 'DateCreated', title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) }
                            ]}
                            students={useServerSearch ? students : students.filter(student => {

                                const lowerSearch = searchTerm.toLowerCase();
                                return (
                                    student.Email?.toLowerCase().includes(lowerSearch)
                                    || student.Fullname?.toLowerCase().includes(lowerSearch)
                                    || student.Username?.toLowerCase().includes(lowerSearch)
                                );

                            })}
                            advancedSearchFields={studentDefaultLookupSearchFields}
                            allowExport={false}
                            loadingGridData={loadingGridData}
                            multiSelect={resolvedMultiSelect}
                            onPreSearchResults={useServerSearch ? undefined : results => setState({ students: results })}
                            onSelectedChange={students => setState({ selectedStudents: students })}
                            onServerPageChange={useServerSearch ? handleServerPageChange : undefined}
                            onServerSortChange={useServerSearch ? handleServerSortChange : undefined}
                            permissions={permissions}
                            searchTerm={searchTerm}
                            selectedStudents={selectedStudents}
                            serverSidePagination={useServerSearch}
                            serverTotalCount={useServerSearch ? serverTotalCount : undefined}
                            setLoadingGridData={((v: boolean) => setState({ loadingGridData: v })) as React.Dispatch<React.SetStateAction<boolean>>}
                            setSearchTerm={term => setState({ searchTerm: term })}
                            setStudents={students => setState({ students })}
                            toggleColumns={false}
                        />
                    </Col>
                </Row>
            </Container>
        </GMetrixDialog>
    );

});

StudentGMetrixTableDialog.displayName = 'StudentGMetrixTableDialog';
